import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { BookingAccessForm } from "@/components/site/BookingAccessForm";
import { MessageCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useApi} from "@/lib/api";
import { PageHero } from "@/components/site/SectionHead";
import { ADDON_KEYS } from "@/components/site/BookingExtras";

export const STATUS_CLS = { pending: "bg-amber-100 text-amber-800", confirmed: "bg-emerald-100 text-emerald-800", completed: "bg-sky-100 text-sky-800", cancelled: "bg-slate-200 text-slate-600" };

function List() {
  const { t, money, tr } = useLang();
  const { data, loading, error, reload } = useApi("/bookings/me");
  if (error) return <div role="alert" data-testid="my-bookings-error" className="space-y-3 rounded-xl bg-rose-50 p-4 text-sm text-rose-700"><p>{error}</p><button data-testid="my-bookings-retry" onClick={reload} className="rounded-full border border-rose-200 px-4 py-2 font-semibold transition-colors hover:bg-rose-100">{t("access_retry")}</button></div>;
  if (loading) return <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#064E3B]" />;
  if (!data?.length) return <p data-testid="no-bookings" className="py-16 text-center text-slate-500">{t("no_bookings")}</p>;
  return (
    <div className="space-y-4">
      {data.map((b) => (
        <div key={b.id} data-testid={`my-booking-${b.code}`} className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-slate-200 bg-white p-5">
          <div>
            <p className="font-mono text-xs text-slate-500">{b.code} · {b.item_type.toUpperCase()}</p>
            <p className="text-lg font-bold">{b.item_name}</p>
            {b.transport_ref && <p data-testid={`booking-transport-${b.code}`} className="text-sm text-slate-600">{t("transport_label")}: <b>{b.transport_ref}</b></p>}
            {b.addons?.length > 0 && <p data-testid={`booking-addons-${b.code}`} className="text-sm text-sky-800">+ {b.addons.map((a) => t(ADDON_KEYS[a])).join(", ")}</p>}
            {b.partner && <p data-testid={`booking-partner-${b.code}`} className="text-sm font-semibold text-[#064E3B]">{t("mb_partner")}: {b.partner.slug ? <Link to={`/mitra/${b.partner.slug}`} className="underline underline-offset-2">{b.partner.name}</Link> : b.partner.name}</p>}
            {b.guide_name && <p data-testid={`booking-guide-${b.code}`} className="text-sm text-[#D97706] font-semibold">{t("addon_guide_short")}: {b.guide_name}</p>}
            <p className="text-sm text-slate-500">{b.date} · {b.quantity}x · {t("total")} <b className="text-slate-900">{b.currency && b.currency !== "IDR" ? `${b.currency} ${Math.round(b.total_foreign).toLocaleString()}` : money(b.total)}</b></p>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid={`my-booking-status-${b.code}`} className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_CLS[b.status]}`}>{t(`status_${b.status}`)}</span>
            <a data-testid={`my-booking-wa-${b.code}`} href={b.whatsapp_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-full bg-[#25D366] px-4 py-2 text-sm font-bold text-white"><MessageCircle className="h-4 w-4" />WhatsApp</a>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MyBookings() {
  const { user, ready } = useAuth();
  const { t } = useLang();
  const [linked, setLinked] = useState(null);
  const [revision, setRevision] = useState(0);
  if (!ready) return null;
  if (!user) return <Navigate to="/login?next=/bookings" replace />;
  return (
    <div data-testid="my-bookings-page">
      <PageHero title={t("my_bookings")} />
      <section className="mx-auto max-w-4xl space-y-6 px-4 sm:px-6">
        {user.role !== "admin" && <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-5 sm:p-6">
          <h2 className="text-base font-bold text-[#064E3B] sm:text-lg">{t(user.phone ? "access_identity" : "access_link")}</h2>
          {user.phone ? <><p data-testid="account-whatsapp-number" className="mt-2 font-mono text-sm font-bold">{user.phone}</p><p data-testid="account-auto-link-hint" className="mt-2 text-sm leading-relaxed text-slate-600">{t("access_auto")}</p></> : <div className="mt-4 max-w-lg"><BookingAccessForm mode="link" onSuccess={(result) => { setLinked(result.linked_count); setRevision((n) => n + 1); }} /></div>}
          {linked !== null && <p role="status" data-testid="booking-link-success" className="mt-4 text-sm font-semibold text-[#064E3B]">{t("access_linked").replace("{n}", linked)}</p>}
        </div>}
        <Link data-testid="my-bookings-check-status" to="/booking-status" className="inline-flex text-sm font-bold text-[#064E3B] underline underline-offset-4">{t("check_booking")}</Link>
        <List key={revision} />
      </section>
    </div>
  );
}
